import { Entity } from '@backstage/catalog-model';
import { SpikeAnnotations } from '@ciandtspike.io/plugin-ciandtspike-common';

// Provider configuration for multicloud support
export const PROVIDER_CONFIG = {
  azure: {
    title: 'Azure Key Vault Secrets',
    displayName: 'Azure Key Vault',
    annotationKey: SpikeAnnotations.AZURE_SECRETS_MANAGER,
  },
  aws: {
    title: 'AWS Secrets Manager',
    displayName: 'AWS Secrets Manager',
    annotationKey: undefined,
  },
  gcp: {
    title: 'Google Secret Manager',
    displayName: 'Google Secret Manager',
    annotationKey: SpikeAnnotations.GCP_SECRETS_MANAGER,
  },
} as const;

export type SupportedProvider = keyof typeof PROVIDER_CONFIG;

/**
 * Extract cloud provider from entity annotations with fallback to Azure
 * @param entity - Backstage entity
 * @returns Supported cloud provider
 */
export const getProvider = (entity: Entity): SupportedProvider => {
  const annotationProvider = entity.metadata.annotations?.[SpikeAnnotations.CLOUD_PROVIDER]?.toLowerCase();
  
  if (annotationProvider && annotationProvider in PROVIDER_CONFIG) {
    return annotationProvider as SupportedProvider;
  }

  throw new Error(`Unsupported cloud provider: ${annotationProvider}`);
};

/**
 * Get provider-specific configuration
 * @param entity - Backstage entity
 * @returns Provider configuration object
 */
export const getProviderConfig = (entity: Entity) => {
  const provider = getProvider(entity);
  return PROVIDER_CONFIG[provider];
};

/**
 * Extract Azure Key Vault name from entity annotations with validation
 * @param entity - Backstage entity
 * @returns Azure Key Vault name
 * @throws Error if annotation is missing or invalid
 */
export const getVaultName = (entity: Entity): string => {
  const providerConfig = getProviderConfig(entity);
  
  // Get the appropriate annotation key for the provider
  const annotationKey = providerConfig.annotationKey;
  const vaultName = annotationKey ? entity.metadata.annotations?.[annotationKey] : entity.metadata.name;
  
  if (!vaultName) {
    throw new Error(`Missing required annotation '${annotationKey}' for ${providerConfig.displayName} integration`);
  }

  return vaultName;
};

/**
 * Generate dynamic table title based on provider
 * @param entity - Backstage entity
 * @returns Table title string
 */
export const getTableTitle = (entity: Entity): string => {
  const providerConfig = getProviderConfig(entity);
  try {
    const secretStoreName = getVaultName(entity);
    return `${providerConfig.title} - ${secretStoreName}`;
  } catch {
    return providerConfig.title;
  }
};

/**
 * Generate dynamic table subtitle based on provider and entity
 * @param entity - Backstage entity
 * @returns Table subtitle string
 */
export const getTableSubtitle = (entity: Entity): string => {
  const providerConfig = getProviderConfig(entity);
  try {
    const secretStoreName = getVaultName(entity);
    return `Secrets for ${entity.metadata.name} via ${providerConfig.displayName} (${secretStoreName})`;
  } catch {
    return `Secrets for ${entity.metadata.name} via ${providerConfig.displayName}`;
  }
};

/**
 * Build query parameters for backend API calls
 * @param entity - Backstage entity
 * @returns URLSearchParams for API requests
 */
export const buildQueryParams = (entity: Entity, secretName: string): URLSearchParams => {
  const provider = getProvider(entity);
  const vaultName = getVaultName(entity);
  const region = entity.metadata.annotations?.[SpikeAnnotations.AWS_REGION] || 'us-east-1';

  return new URLSearchParams({
    provider,
    vaultName,
    region,
    secretName,
    cloudInstanceName: entity.metadata.name,
    // Include entity context for backend validation
    entityRef: `${entity.kind}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`,
  });
};